import React, { useRef } from 'react';
import { FieldArray, Form, Formik, useFormikContext } from 'formik';
import { noop } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { MainCard, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import { PlusIcon, TrashIcon } from '~/components/icons';
import TextFieldFormik, { DatePickerTextFieldFormik } from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { createCapacityAgreement, updateCapacityAgreement } from '../apiService';
import { SLOT_DETAILS_MAX_LENGTH, SLOT_DETAILS_MAX_ROWS } from '../constants';
import { TEXTS } from '../texts';
import type { CapacityAgreementJsonType, CapacityAgreementType, CapacityAgreementUpdateJsonType } from '../types';

type CapacityAgreementFormValuesType = Omit<CapacityAgreementJsonType, 'capacity_slots'> & {
  capacity_slots_updates: NonNullable<CapacityAgreementUpdateJsonType['capacity_slots_updates']>;
  capacity_slots_additions: (NonNullable<CapacityAgreementUpdateJsonType['capacity_slots_additions']>[number] & {
    /**
     * used to ensure each newly added capacity slot has a unique key for react mapping,
     * as the index is not reliable for dynamic lists.
     * Ignored server-side.
     * */
    uniqueKey?: number;
  })[];
};

const ONE_DAY = 24 * 60 * 60 * 1000;

interface BaseProps {
  open: boolean;
  onClose?: () => void;
}

interface AddUpdateCapacityAgreementDialogProps extends BaseProps {
  onUpdateSuccess?: () => Promise<void> | void;
  capacityAgreement: CapacityAgreementType | null;
  policyId: number;
  organizationId: number;
  isManualPolicy: boolean;
}

interface AddUpdateCapacityAgreementDialogInnerProps extends BaseProps {
  title: string;
}

const capacitySlotsSchema = Yup.array().of(
  Yup.object().shape({
    external_id: Yup.string().nullable(),
    slot_name: Yup.string().nullable().trim().required(TEXTS.required),
    details: Yup.string().nullable().trim().max(SLOT_DETAILS_MAX_LENGTH, TEXTS.slotDetailsMaxLength),
  })
);

const validationSchema = Yup.object().shape({
  external_id: Yup.string().nullable().trim(),
  agreement_number: Yup.string().nullable().trim().required(TEXTS.required),
  umr: Yup.string().nullable(),
  lloyds_risk_code: Yup.string().nullable().trim().max(2, TEXTS.lloydsRiskCodeMaxLength),
  carrier_name: Yup.string().nullable().trim().required(TEXTS.required),
  effective_date: Yup.date().nullable().required(TEXTS.required),
  expiration_date: Yup.date()
    .nullable()
    .required(TEXTS.required)
    .when('effective_date', (effectiveDate, schema) => {
      if (!effectiveDate) {
        return schema;
      }
      const minDate = new Date(Number(effectiveDate) + ONE_DAY);
      return schema.min(minDate, TEXTS.expirationDateMustBeAfterEffectiveDate);
    }),
  capacity_slots_updates: capacitySlotsSchema,
  capacity_slots_additions: capacitySlotsSchema,
});

const getInitialValues = (capacityAgreement: CapacityAgreementType | null): CapacityAgreementFormValuesType => {
  return {
    external_id: capacityAgreement?.external_id || null,
    agreement_number: capacityAgreement?.agreement_number || '',
    umr: capacityAgreement?.umr || '',
    lloyds_risk_code: capacityAgreement?.lloyds_risk_code || '',
    carrier_name: capacityAgreement?.carrier_name || '',
    effective_date: capacityAgreement?.effective_date || '',
    expiration_date: capacityAgreement?.expiration_date || '',
    capacity_slots_updates:
      capacityAgreement?.capacity_slots.map((slot) => ({
        external_id: slot.external_id,
        slot_name: slot.slot_name,
        details: slot.details ?? '',
      })) || [],
    capacity_slots_additions: [],
  };
};

const CapacitySlotEditRow = ({
  index,
  isUpdate,
  onRemove,
}: {
  index: number;
  isUpdate: boolean;
  onRemove: () => void;
}) => {
  const fieldPrefix = isUpdate ? 'capacity_slots_updates' : 'capacity_slots_additions';
  return (
    <div className={cn('col-span-2 grid items-end gap-24', isUpdate ? 'grid-cols-2' : 'grid-cols-[1fr_1fr_auto]')}>
      <TextFieldFormik id={`${fieldPrefix}.${index}.slot_name`} label={TEXTS.slotsFieldLabels.slot_name} />
      <TextFieldFormik
        id={`${fieldPrefix}.${index}.details`}
        label={TEXTS.slotsFieldLabels.details}
        multiline
        maxRows={SLOT_DETAILS_MAX_ROWS}
      />
      {isUpdate ? null : (
        <IconButton onClick={onRemove} className="-ml-12" disabled={isUpdate}>
          <TrashIcon />
          <span className="sr-only">{TEXTS.removeSlot()}</span>
        </IconButton>
      )}
    </div>
  );
};

function CapacitySlotsFormSection({
  existingCapacitySlots,
  capacitySlotsAdditions,
}: {
  existingCapacitySlots: CapacityAgreementFormValuesType['capacity_slots_updates'];
  capacitySlotsAdditions: CapacityAgreementFormValuesType['capacity_slots_additions'];
}) {
  const anySlotsDisplayed = existingCapacitySlots.length > 0 || capacitySlotsAdditions.length > 0;
  const uniqueKeyRef = useRef(1);

  return (
    <FieldArray name="capacity_slots_additions">
      {({ push, remove }) => {
        const addSlotbutton = (
          <Button
            type="button"
            variant="text"
            color="primary"
            className="self-end"
            onClick={() => {
              push({ external_id: null, slot_name: '', details: '', uniqueKey: uniqueKeyRef.current });
              uniqueKeyRef.current += 1;
            }}
          >
            <PlusIcon className="mb-2 mr-4" iconColor="currentColor" size={14} />
            {TEXTS.addSlot()}
          </Button>
        );

        return (
          <MainCard type="contained" className="col-span-2 border-slate-500 p-16">
            <div className="flex items-center justify-between gap-12">
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                {TEXTS.slotsTitle()}
              </Text>
              {anySlotsDisplayed ? null : addSlotbutton}
            </div>
            {existingCapacitySlots.map((slot, index) => (
              <CapacitySlotEditRow key={slot.external_id} index={index} isUpdate onRemove={noop} />
            ))}
            {capacitySlotsAdditions.map((slot, index) => (
              <CapacitySlotEditRow key={slot.uniqueKey} index={index} isUpdate={false} onRemove={() => remove(index)} />
            ))}
            {anySlotsDisplayed ? addSlotbutton : null}
          </MainCard>
        );
      }}
    </FieldArray>
  );
}

function AddUpdateCapacityAgreementDialogInner({ open, onClose, title }: AddUpdateCapacityAgreementDialogInnerProps) {
  const { values, submitForm, isSubmitting } = useFormikContext<CapacityAgreementFormValuesType>();
  const footerActions = (
    <div className="flex w-full gap-16 px-16 pb-16">
      <ErrorHelperTextFormik id="capacity_slots_additions" withoutChildren className="self-end" />
      <Button
        type="button"
        variant="outlined"
        color="primary"
        onClick={onClose}
        className="ml-auto"
        disabled={isSubmitting}
      >
        {TEXTS.cancel}
      </Button>
      <Button type="button" variant="contained" color="primary" onClick={submitForm} disabled={isSubmitting}>
        {TEXTS.submit}
      </Button>
    </div>
  );

  return (
    <CardDialog isDialog open={open} onClose={onClose} title={title} footerActions={footerActions} fullWidth>
      <Form className="grid grid-cols-2 gap-24">
        <TextFieldFormik id="carrier_name" label={TEXTS.capacityFieldLabels.carrier_name()} />
        <TextFieldFormik id="agreement_number" label={TEXTS.capacityFieldLabels.agreement_number()} />
        <DatePickerTextFieldFormik
          id="effective_date"
          label={TEXTS.capacityFieldLabels.effective_date()}
          fullWidth
          clearable
        />
        <DatePickerTextFieldFormik
          id="expiration_date"
          label={TEXTS.capacityFieldLabels.expiration_date()}
          fullWidth
          clearable
        />
        <TextFieldFormik id="umr" label={TEXTS.capacityFieldLabels.umr()} />
        <TextFieldFormik id="lloyds_risk_code" label={TEXTS.capacityFieldLabels.lloyds_risk_code()} />
        <CapacitySlotsFormSection
          existingCapacitySlots={values.capacity_slots_updates}
          capacitySlotsAdditions={values.capacity_slots_additions}
        />
        {/* Hidden button to trigger form submission with the Enter key
        (needed due to displayed and clickable button being rendered outside of the actual form element) */}
        <button type="submit" className="sr-only" tabIndex={-1} disabled={isSubmitting} />
      </Form>
    </CardDialog>
  );
}

// `details` is a nullable column, so send an explicit `null` for a blank field rather than leaning
// on the server coercing `''` to None. Trim first, so the payload carries what the server stores.
const withNullifiedDetails = <T extends { details?: string | null }>(slot: T): T => ({
  ...slot,
  details: slot.details?.trim() || null,
});

const applyCommonValuesTransformations = (
  formValues: CapacityAgreementFormValuesType
): CapacityAgreementFormValuesType => ({
  ...formValues,
  umr: formValues.umr || null,
  lloyds_risk_code: formValues.lloyds_risk_code || null,
  capacity_slots_updates: formValues.capacity_slots_updates.map(withNullifiedDetails),
  capacity_slots_additions: formValues.capacity_slots_additions.map(withNullifiedDetails),
});

function AddUpdateCapacityAgreementDialog({
  open,
  onClose,
  onUpdateSuccess,
  capacityAgreement,
  policyId,
  isManualPolicy,
  organizationId,
}: AddUpdateCapacityAgreementDialogProps) {
  const isUpdate = !!capacityAgreement;
  const initialValues = getInitialValues(capacityAgreement);

  const handleSubmit = async (addOrUpdate: () => Promise<void>) => {
    let isApiSuccess = false;
    try {
      await addOrUpdate();
      isApiSuccess = true;
      if (onUpdateSuccess) {
        await onUpdateSuccess();
      }
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      if (isApiSuccess) {
        onClose?.();
      }
    }
  };

  const create = async (formValues: CapacityAgreementFormValuesType) => {
    const values = applyCommonValuesTransformations(formValues);
    const { capacity_slots_updates, capacity_slots_additions, ...restValues } = values;
    const json = {
      ...restValues,
      capacity_slots: [...(capacity_slots_additions || [])],
    };
    await handleSubmit(async () => {
      await createCapacityAgreement({ organizationId, policyId, json, isManualPolicy });
    });
  };

  const update = async (formValues: CapacityAgreementFormValuesType) => {
    const values = applyCommonValuesTransformations(formValues);
    const { external_id, ...restValues } = values;
    if (!external_id) {
      // This should never happen in update mode, but for typescript's sake (and just in case)
      throw new Error('External ID is required for update');
    }
    await handleSubmit(async () => {
      await updateCapacityAgreement({ organizationId, policyId, json: { ...restValues, external_id }, isManualPolicy });
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={isUpdate ? update : create}
      enableReinitialize
    >
      <AddUpdateCapacityAgreementDialogInner
        open={open}
        onClose={onClose}
        title={capacityAgreement ? TEXTS.updateDialogTitle() : TEXTS.addDialogTitle()}
      />
    </Formik>
  );
}

export default AddUpdateCapacityAgreementDialog;
